import type { ReactNode } from "react";
import Svg, { Line } from "react-native-svg";
import { useTheme } from "tamagui";
import { Check as CheckIcon } from "@tamagui/lucide-icons";
import { type DisplayFontSizeKeys, displayLineHeight, type KhTekaFontSizeKeys, khTekaLineHeight } from "../themes/token-fontSizes";
import { sizeTokens } from "../themes/token-size";
import { Circle, XStack, YStack } from "./Stacks";
const RAIL_DASH = "6 4";
const LINE_HEIGHT = {
  ...khTekaLineHeight,
  ...displayLineHeight
};
// Read raw: this module loads before the Tamagui config exists.
const DOT_SIZE = sizeTokens.si3;
const CURRENT = {
  brand: {
    fill: "$bgBrandIntense",
    halo: "bgBrand",
    centre: "$iconPositive"
  },
  warning: {
    fill: "$bgWarningStrong",
    halo: "bgWarning",
    centre: "$iconWarning"
  }
} as const;
const STATUS_LABEL = {
  done: "Done",
  current: "Current",
  todo: "Upcoming"
} as const;
export type RailStep = {
  key: string;
  /**
   * More than one step can be current when the screen can't tell which; give
   * each a `label` such as "Maybe current". `aria-current` marks a sole one only.
   */
  status: keyof typeof STATUS_LABEL;
  /** What a screen reader says for the marker, when the status word isn't right. */
  label?: string;
  content: ReactNode;
  testID?: string;
  tone?: keyof typeof CURRENT;
  marker?: ReactNode;
  firstLine?: KhTekaFontSizeKeys | DisplayFontSizeKeys;
};
export function RailStepper({
  steps,
  rowGap = "$sp2.5",
  indent = "$sp1.5",
  dashed = false
}: {
  steps: RailStep[];
  rowGap?: "$sp2.5" | "$sp4";
  indent?: "$sp1" | "$sp1.5";
  dashed?: boolean;
}) {
  const sole = steps.filter(step => step.status === "current").length === 1;
  return <YStack role="list" data-sentry-element="YStack" data-sentry-component="RailStepper" data-sentry-source-file="RailStepper.tsx">
      {steps.map((step, i) => {
      const last = i === steps.length - 1;
      const dot = step.marker ? step.marker : step.status === "current" ? <RailCurrentDot tone={step.tone} /> : step.status === "done" ? <RailDoneDot /> : <HollowDot />;
      const line = LINE_HEIGHT[step.firstLine ?? "body3"];
      const lineBox = Math.max(line, DOT_SIZE);
      return <XStack key={step.key} role="listitem" aria-current={sole && step.status === "current" ? "step" : undefined} gap={indent} testID={step.testID}>
            <YStack alignItems="center" width="$si3">
              <YStack role="img" accessible aria-label={step.label ?? STATUS_LABEL[step.status]} height={lineBox} alignItems="center" justifyContent="center" zIndex={1}>
                {dot}
              </YStack>
              {last ? null : dashed && step.status === "todo" ? <DashedRail /> : <>
                  <YStack width="$si0.25" flex={1} backgroundColor="$borderDefault" />
                  {dashed ? <DashedRail height="$si2" /> : null}
                </>}
            </YStack>
            <YStack flex={1} paddingTop={(lineBox - line) / 2} paddingBottom={last ? 0 : rowGap}>
              {step.content}
            </YStack>
          </XStack>;
    })}
    </YStack>;
}
function DashedRail({
  height
}: {
  height?: "$si2";
}) {
  const theme = useTheme();
  return <YStack width="$si0.25" flex={height ? undefined : 1} height={height} data-sentry-element="YStack" data-sentry-component="DashedRail" data-sentry-source-file="RailStepper.tsx">
      {/* An SVG with no definite height is 150px tall on web. */}
      <YStack position="absolute" top={0} bottom={0} left={0} right={0} data-sentry-element="YStack" data-sentry-source-file="RailStepper.tsx">
        <Svg width="100%" height="100%" data-sentry-element="Svg" data-sentry-source-file="RailStepper.tsx">
          <Line x1="50%" y1="0" x2="50%" y2="100%" stroke={theme.borderDefault.val} strokeWidth={2} strokeDasharray={RAIL_DASH} data-sentry-element="Line" data-sentry-source-file="RailStepper.tsx" />
        </Svg>
      </YStack>
    </YStack>;
}

/** The rail's marker for the step you're on. */
export function RailCurrentDot({
  tone = "brand"
}: {
  tone?: keyof typeof CURRENT;
}) {
  const theme = useTheme();
  const {
    fill,
    halo,
    centre
  } = CURRENT[tone];
  return <Circle size="$si3" backgroundColor={fill} boxShadow={`0 0 0 4px ${theme[halo].val}`} data-sentry-element="Circle" data-sentry-component="RailCurrentDot" data-sentry-source-file="RailStepper.tsx">
      <Circle size="$si1" backgroundColor={centre} data-sentry-element="Circle" data-sentry-source-file="RailStepper.tsx" />
    </Circle>;
}

/** The rail's marker for a step that's done. */
export function RailDoneDot() {
  return <Circle size="$si3" backgroundColor="$bgBrandStrong" data-sentry-element="Circle" data-sentry-component="RailDoneDot" data-sentry-source-file="RailStepper.tsx">
      <CheckIcon size="$iconXSmall" color="$iconPositive" data-sentry-element="CheckIcon" data-sentry-source-file="RailStepper.tsx" />
    </Circle>;
}
function HollowDot() {
  return <Circle size="$si3" borderWidth="$border2" borderColor="$borderMedium" backgroundColor="$surface2" data-sentry-element="Circle" data-sentry-component="HollowDot" data-sentry-source-file="RailStepper.tsx" />;
}